import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { orderAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiPackage, FiPlus, FiFilter, FiRefreshCw } from 'react-icons/fi';
import './Orders.css';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ platform: '', status: '' });

  useEffect(() => {
    fetchOrders();
  }, [filter]);

  const fetchOrders = async () => {
    try {
      const params = {};
      if (filter.platform) params.platform = filter.platform;
      if (filter.status) params.status = filter.status;

      const response = await orderAPI.getAll(params);
      setOrders(response.data.orders || []);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      swiggy: '🍔',
      zomato: '🍕',
      bigbasket: '🛒',
      blinkit: '⚡'
    };
    return icons[platform] || '📦';
  };

  const getStatusColor = (status) => {
    const colors = {
      placed: 'info',
      confirmed: 'info',
      preparing: 'warning',
      picked_up: 'warning',
      out_for_delivery: 'warning',
      near_location: 'success',
      delivered: 'success',
      cancelled: 'danger'
    };
    return colors[status] || 'info';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="orders-page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1>My Orders</h1>
            <p>Track all your deliveries in one place</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-outline" onClick={fetchOrders}>
              <FiRefreshCw /> Refresh
            </button>
            <Link to="/orders/new" className="btn btn-primary">
              <FiPlus /> Register Order
            </Link>
          </div>
        </div>

        <div className="filters">
          <div className="filter-group">
            <FiFilter />
            <select
              value={filter.platform}
              onChange={(e) => setFilter(prev => ({ ...prev, platform: e.target.value }))}
            >
              <option value="">All Platforms</option>
              <option value="swiggy">Swiggy</option>
              <option value="zomato">Zomato</option>
              <option value="bigbasket">BigBasket</option>
              <option value="blinkit">Blinkit</option>
            </select>
          </div>
          <div className="filter-group">
            <select
              value={filter.status}
              onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="">All Status</option>
              <option value="placed">Placed</option>
              <option value="confirmed">Confirmed</option>
              <option value="preparing">Preparing</option>
              <option value="picked_up">Picked Up</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="empty-state card">
            <FiPackage className="empty-icon" />
            <h3>No orders found</h3>
            <p>Register an order to start tracking</p>
            <Link to="/orders/new" className="btn btn-primary">
              <FiPlus /> Register Your First Order
            </Link>
          </div>
        ) : (
          <div className="orders-list">
            {orders.map(order => (
              <Link to={`/orders/${order._id}`} key={order._id} className="order-item card">
                <div className="order-main">
                  <div className="order-platform">
                    <span className="platform-icon">{getPlatformIcon(order.platform)}</span>
                    <div>
                      <span className="platform-name">{order.platform}</span>
                      <span className="order-id">#{order.platformOrderId}</span>
                    </div>
                  </div>
                  <span className={`badge badge-${getStatusColor(order.status)}`}>
                    {order.status.replace(/_/g, ' ')}
                  </span>
                </div>

                {order.rider?.name && (
                  <div className="order-rider">
                    <span>Rider: {order.rider.name}</span>
                    {order.rider.vehicleNumber && (
                      <span> • {order.rider.vehicleNumber}</span>
                    )}
                  </div>
                )}

                <div className="order-meta">
                  <span>{formatDate(order.createdAt)}</span>
                  {order.deliveryLocation?.label && (
                    <span>• {order.deliveryLocation.label}</span>
                  )}
                  {order.instructionsShared && (
                    <span className="badge badge-success">Instructions Shared</span>
                  )}
                  {order.gatePass && (
                    <span className="badge badge-info">Gate Pass</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
